import {
  createSessionKey,
  decryptText,
  generateAESKey,
} from '@/common/utils/crypto';
import {
  ICard,
  TCardDetails,
  TCardDetailsEncrypted,
  TCardDetailsUrl,
  TNormalizedCardDetails,
} from './card.types';

export function isEncryptedCardDetails(
  details: TCardDetails
): details is TCardDetailsEncrypted {
  return 'iv' in details.cvv && 'iv' in details.pan;
}

export function isExternalCardDetails(
  details: TCardDetails
): details is TCardDetailsUrl {
  return (
    details.cvv && 'url' in details.cvv && details.pan && 'url' in details.pan
  );
}

export const generateCardDetailsSessionKey = async (
  publicKey: string
): Promise<{ encKey: CryptoKey; sessionKey: string }> => {
  const encKey = await generateAESKey();
  const sessionKey = await createSessionKey(encKey, publicKey);
  return {
    encKey,
    sessionKey,
  };
};

export async function decryptDetails(
  key: CryptoKey,
  details: TCardDetailsEncrypted
): Promise<TNormalizedCardDetails> {
  const pan = await decryptText(key, details.pan.iv, details.pan.secret);
  const cvv = await decryptText(key, details.cvv.iv, details.cvv.secret);
  const expMonth = `${details.expiration.month}`.padStart(2, '0');
  const expYear = `${details.expiration.year}`.slice(2, 4);

  return {
    pan: {
      type: 'number',
      value: pan,
    },
    cvv: {
      type: 'number',
      value: cvv,
    },
    exp: `${expMonth}/${expYear}`,
  };
}

export function buildExternalDetails(
  details: TCardDetailsUrl,
  cardStatus: ICard['status']
): TNormalizedCardDetails {
  const expMonth = `${details.expiration.month}`.padStart(2, '0');
  const expYear = `${details.expiration.year}`.slice(2, 4);
  const queryParams = `&bgcolor=${cardStatus === 'locked' ? 'E8F6FB' : 'FF9878'}&fontcolor=${cardStatus === 'locked' ? '368CB8' : 'FFFFFF'}&fontsize=x-large&fontfamily=helvetica`;
  const panUrl = details.pan.url;
  const cvvUrl = details.cvv.url;

  return {
    pan: {
      type: 'url',
      value: `${panUrl}${queryParams}`,
    },
    cvv: {
      type: 'url',
      value: `${cvvUrl}${queryParams}`,
    },
    exp: `${expMonth}/${expYear}`,
  };
}
